
import { defineComponent, computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import BaseLink from './BaseLink.vue';

export default defineComponent({
  name: 'LazyBaseLink',

  components: {
    BaseLink,
  },

  props: {
    // Hériter de toutes les props de BaseLink
    modelValue: {
      type: [String, Object],
      required: true,
    },
    variablesList: {
      type: Array,
      required: false,
      default: () => [],
    },
    linkTypeOptions: {
      type: Array,
      required: true,
    },
    originCall: {
      type: String,
      required: false,
      default: null,
    },
    idCampaign: {
      type: Number,
      required: false,
      default: null,
    },
    translation: {
      type: Object,
      required: false,
      default: null,
    },
    // Props spécifiques au lazy loading
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue', 'click', 'activate-group', 'deactivate-group'],

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Aperçu du lien
    const linkPreview = computed(() => {
      if (!props.modelValue) return '';
      
      if (typeof props.modelValue === 'string') {
        return props.modelValue.length > 50 ? `${props.modelValue.substring(0, 50)}...` : props.modelValue;
      }
      
      if (typeof props.modelValue === 'object') {
        if (props.modelValue.url) {
          const url = props.modelValue.url;
          return url.length > 50 ? `${url.substring(0, 50)}...` : url;
        }
        if (props.modelValue.text) {
          const text = props.modelValue.text;
          return text.length > 50 ? `${text.substring(0, 50)}...` : text;
        }
      }
      
      return 'Lien configuré';
    });

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadComponent();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      loadComponent,
      linkPreview,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
